/*!
 * Copyright 2022 Quisitive Technologies, LLC. All rights reserved.
 */
import SocialHistoryImage from '../assets/icons/social_history/social_history_full.png';
import SocialHistoryOne from '../assets/icons/social_history/social_history_beer_mobile.png';
import SocialHistoryTwo from '../assets/icons/social_history/social_history_malt_mobile.png';
import SocialHistoryThree from '../assets/icons/social_history/social_history_wine_mobile.png';
import SocialHistoryFour from '../assets/icons/social_history/social_history_spirits_mobile.png';
import medications from '../jsons/medicationList.json';
import cerealsList from '../jsons/cerealsList.json';

export default {
  familyHistory: {
    title: 'Family History',
    route: '/me/family-history',
    shortName: 'familyHistory',
    completed: false,
    order: 1,
    timeToComplete: '2min',
    _meta: {
      route: '/landing/assessments/familyHistory'
    },
    steps: [
      {
        stepId: 'family_0',
        completed: false,
        questions: [
          {
            type: 'CheckboxGrid',
            questionId: 'family_YTBe4l',
            details: {
              page: 0,
              notRequired: true,
              noneOfAboveText: 'Check here if none of the above apply.',
              question:
                'Please select which, if any, of your blood relatives have or ever had the following (select all affected).',
              yLabels: [
                'Cancer',
                'Heart Disease',
                'Elevated Blood Pressure',
                'Diabetes',
                'Elevated Cholesterol',
                'Stroke',
                'Lung Disease',
                'Thyroid Disease',
                'Osteoporosis',
                'Dementia'
              ],
              xLabels: [
                'Mother',
                'Father',
                'Maternal Grandparent',
                'Paternal Grandparent',
                'Sister or Brother',
                'N/A'
              ],
              yToolTips: {
                'Heart Disease':
                  'Including any family history of CAD such as heart attack and/or hardening of the arteries (especially if any 1st degree relatives were < 55 y in a male or < 65 y in a female first-degree relative)'
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'family_TsZoUp',
            details: {
              horizontal: true,
              question:
                'Do any of your blood relatives have any other significant medical history you would like to report?',
              options: ['Yes', 'No'],
              routeMap: {
                Yes: { show: ['family_1qLCr5', 'family_dI54OE'] },
                No: { hide: ['family_1qLCr5', 'family_dI54OE'] }
              }
            }
          },
          {
            type: 'SingleText',
            questionId: 'family_1qLCr5',
            display: false,
            details: {
              question:
                'Please describe the other significant medical history.',
              text: {
                size: 'medium'
              }
            }
          },
          {
            type: 'Checkbox',
            questionId: 'family_dI54OE',
            display: false,
            details: {
              question:
                'Please select all relatives affected by the condition.',
              options: [
                'Mother',
                'Father',
                'Maternal Grandparent',
                'Paternal Grandparent',
                'Sister or Brother',
                'None',
                'Unknown'
              ]
            }
          }
        ]
      }
    ],
    answers: {
      example: {
        medicalConditions: {
          highBloodPleasure: ['mother', 'father', 'paternal Grandparent'],
          highCholesterol: ['mother', 'paternal Grandparent'],
          depression: ['father', 'paternal Grandparent'],
          dementia: ['mother', 'father', 'paternal Grandparent'],
          diabetes: ['mother', 'paternal Grandparent'],
          heartDisease: ['sibling', 'father', 'maternal Grandparent'],
          lungDisease: ['mother', 'paternal Grandparent'],
          stroke: ['mother'],
          thyroid: ['sibling', 'paternal Grandparent'],
          other: ['mother', 'father', 'paternal Grandparent']
        },
        cancers: {
          bladder: ['mother', 'father', 'paternal Grandparent'],
          brain: ['mother', 'paternal Grandparent'],
          breast: ['mother', 'father'],
          colon: ['mother'],
          leukemia: ['mother', 'paternal Grandparent'],
          liver: ['mother', 'paternal Grandparent'],
          lung: ['sibling', 'father', 'maternal Grandparent'],
          lymphoma: ['father', 'maternal Grandparent'],
          melanoma: ['mother', 'father'],
          pancreatic: ['mother', 'father', 'paternal Grandparent'],
          prostate: ['mother', 'paternal Grandparent'],
          thyroid: ['paternal Grandparent'],
          other: ['mother']
        }
      },
      raw: [],
      parsed: {}
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  socialHistory: {
    title: 'Social History',
    route: '/me/social-history',
    shortName: 'socialHistory',
    completed: false,
    order: 2,
    timeToComplete: '2min',
    _meta: {
      route: '/landing/assessments/socialHistory'
    },
    steps: [
      {
        stepId: 'social_step_0',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'social_9VFL5S',
            details: {
              page: 0,
              question: 'Do you currently smoke tobacco?',
              options: ['Yes', 'No'],
              horizontal: true,
              routeMap: {
                Yes: {
                  show: ['social_HUGvM0', 'social_zLoCcn'],
                  hide: ['social_HUGvM1', 'social_HUGvM2']
                },
                No: {
                  hide: ['social_HUGvM0', 'social_zLoCcn', 'social_ko9tvC'],
                  show: ['social_HUGvM1']
                }
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'social_HUGvM1',
            display: false,
            details: {
              page: 1,
              question: 'Did you used to smoke and quit?',
              options: ['Yes', 'No'],
              horizontal: true,
              routeMap: {
                Yes: {
                  show: ['social_HUGvM2']
                },
                No: {
                  hide: ['social_HUGvM2']
                }
              }
            }
          },
          {
            type: 'Dropdown',
            questionId: 'social_HUGvM2',
            display: false,
            details: {
              page: 0,
              question: 'When did you quit?',
              size: 'medium',
              searchable: false,
              placeholder: 'Select quit year',
              options: [
                'I quit within the last year(12 months)',
                'I quit 1-5 years ago',
                'I quit 5-10 years ago',
                'I quit 10-20 years ago',
                'I quit over 20 years ago'
              ]
            }
          },
          {
            type: 'Radio',
            questionId: 'social_HUGvM0',
            display: false,
            details: {
              page: 1,
              question: 'How often?',
              options: ['Daily', 'Less than daily'],
              horizontal: true,
              routeMap: {
                Daily: {
                  show: ['social_ko9tvC']
                },
                'Less than daily': {
                  hide: ['social_ko9tvC']
                }
              }
            }
          },
          {
            type: 'Dropdown',
            questionId: 'social_ko9tvC',
            display: false,
            details: {
              page: 0,
              question: 'How many cigarettes do you smoke per day?',
              size: 'medium',
              searchable: false,
              placeholder: 'Select smoke per day',
              options: [
                'less than 1/2 pack per day',
                '1/2 pack to 1 pack per day',
                '1 pack to 2 packs per day',
                'greater than 2 packs per day'
              ]
            }
          },
          {
            type: 'Radio',
            questionId: 'social_zLoCcn',
            display: false,
            details: {
              page: 0,
              question:
                'Please select the option that best describes your current situation regarding your Smoking use and the approximate number of years you have used.',
              options: [
                'I currently smoke products (less than 5 years).',
                'I currently smoke products (6-10 years).',
                'I currently smoke products (10-15 years).',
                'I currently smoke products (15-20 years)',
                'I currently smoke products (more than 20 years).',
                'I have only smoked a handful of times or less.',
                'I smoke very sporadically and/or on rare occasions.'
              ]
            }
          }
        ]
      },
      {
        stepId: 'social_step_1',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'social_pX8mAS',
            details: {
              page: 0,
              question:
                'Do you use any other Nicotine products such as pipe, Hookahs, Cigars, Chew, or E-Cigarette/Vaping devices?',
              options: ['Yes', 'No'],
              horizontal: true
            }
          }
        ]
      },
      {
        stepId: 'social_step_2',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              page: 2,
              mobileImages: [
                {
                  src: SocialHistoryOne,
                  alt: 'alcohol chart'
                },
                {
                  src: SocialHistoryTwo,
                  alt: 'alcohol chart'
                },
                {
                  src: SocialHistoryThree,
                  alt: 'alcohol chart'
                },
                {
                  src: SocialHistoryFour,
                  alt: 'alcohol chart'
                }
              ],
              message:
                'The following questions ask about your alcohol intake. <br /> When responding, consider the amounts below as examples of 1 drink.',
              image: {
                src: SocialHistoryImage,
                alt: 'alcohol chart'
              },
              subText: `The percent of "pure" alcohol, expressed here as alcohol by volume (alc/vol), varies by beverage.`
            }
          }
        ]
      },
      {
        stepId: 'social_step_3',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'social_kaMeD1',
            details: {
              page: 3,
              question: 'How often do you have a drink containing alcohol?',
              options: [
                'Never',
                'Monthly or less',
                '2-4 times a month',
                '2-3 times a week',
                '4 or more times a week'
              ],
              routeMap: {
                Never: {
                  hide: [
                    'social_770hB1',
                    'social_D04iXr',
                    'social_pY1AXx',
                    'social_L2HCmP',
                    'social_qs99aW',
                    'social_MJQzqj'
                  ]
                },
                'Monthly or less': {
                  show: [
                    'social_770hB1',
                    'social_D04iXr',
                    'social_pY1AXx',
                    'social_L2HCmP',
                    'social_qs99aW',
                    'social_MJQzqj'
                  ]
                },
                '2-4 times a month': {
                  show: [
                    'social_770hB1',
                    'social_D04iXr',
                    'social_pY1AXx',
                    'social_L2HCmP',
                    'social_qs99aW',
                    'social_MJQzqj'
                  ]
                },
                '2-3 times a week': {
                  show: [
                    'social_770hB1',
                    'social_D04iXr',
                    'social_pY1AXx',
                    'social_L2HCmP',
                    'social_qs99aW',
                    'social_MJQzqj'
                  ]
                },
                '4 or more times a week': {
                  show: [
                    'social_770hB1',
                    'social_D04iXr',
                    'social_pY1AXx',
                    'social_L2HCmP',
                    'social_qs99aW',
                    'social_MJQzqj'
                  ]
                }
              },
              fullWidth: true
            }
          },
          {
            type: 'Radio',
            questionId: 'social_770hB1',
            display: false,
            details: {
              page: 4,
              question:
                'How many drinks containing alcohol do you have on a typical day?',
              options: [
                '0',
                '1 or 2',
                '3 or 4',
                '5 or 6',
                '7 to 9',
                '10 or more'
              ],
              fullWidth: true
            }
          },
          {
            type: 'Radio',
            questionId: 'social_D04iXr',
            display: false,
            details: {
              page: 5,
              question:
                'How often do you have six or more drinks on one occasion?',
              options: [
                'Never',
                'Less than monthly',
                'Monthly',
                'Weekly',
                'Daily or almost daily'
              ],
              fullWidth: true
            }
          },
          {
            type: 'Radio',
            questionId: 'social_pY1AXx',
            display: false,
            details: {
              page: 5,
              question:
                'Have you ever felt you should cut down on your drinking?',
              options: ['Yes', 'No'],
              horizontal: true
            }
          },
          {
            type: 'Radio',
            questionId: 'social_L2HCmP',
            display: false,
            details: {
              page: 5,
              question: 'Have people annoyed you by criticizing your drinking?',
              options: ['Yes', 'No'],
              horizontal: true
            }
          },
          {
            type: 'Radio',
            questionId: 'social_qs99aW',
            display: false,
            details: {
              page: 5,
              question: 'Have you ever felt bad or guilty about your drinking?',
              options: ['Yes', 'No'],
              horizontal: true
            }
          },
          {
            type: 'Radio',
            questionId: 'social_MJQzqj',
            display: false,
            details: {
              page: 5,
              question:
                'Have you ever had a drink first thing in the morning to steady your nerves or to get rid of a hangover (eye-opener)?',
              options: ['Yes', 'No'],
              horizontal: true
            }
          }
        ]
      }
    ],
    questions: [],
    answers: {
      example: {
        smoking: { current: 'Less than daily', past: 'Daily' },
        alcohol: {
          frequency: 'Monthly or less',
          quantity: '1 or 2',
          heavyDrinking: 'Monthly'
        }
      },
      raw: [],
      parsed: {}
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  medicalHistory: {
    title: 'Medical History',
    route: '/me/medical-history',
    shortName: 'medicalHistory',
    completed: false,
    order: 3,
    timeToComplete: '5min',
    _meta: {
      route: '/landing/assessments/medicalHistory'
    },
    steps: [
      {
        stepId: 'medical_step_0',
        completed: false,
        questions: [
          {
            type: 'RadioGrid',
            questionId: 'medical_HVOLvK',
            details: {
              page: 4,
              question:
                'Please fill out the below chart regarding your current and past medical history.',
              xLabels: [
                'Currently being treated for or active',
                'In the past & resolved',
                'Never',
                'N/A (for gender-specific questions)'
              ],
              yLabels: [
                'Adult Acne',
                'Hair Loss',
                'Back Issues',
                'Chronic Pain (from any other reasons)',
                'Asthma',
                'COPD',
                'Seasonal Allergies',
                'Ever had Covid',
                'Stroke',
                'Parkinson’s Disease',
                'Alzheimer’s Disease',
                'Multiple Sclerosis',
                'Other Dementia',
                'Heart Attack'
              ],
              routeMap: {
                false: { hide: ['medical_UzN1YM', 'medical_XzM1IB'] },
                true: { show: ['medical_UzN1YM'] }
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'medical_UzN1YM',
            display: false,
            details: {
              page: 5,
              question:
                'Have you ever been seen by a primary care physician as an adult?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['medical_XzM1IB'] },
                Yes: { show: ['medical_XzM1IB'] }
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'medical_XzM1IB',
            display: false,
            details: {
              page: 5,
              question: 'Your reason for not having a primary care physician',
              options: [
                'Can’t find one near me or can’t get a convenient appointment',
                'Don’t have insurance/good insurance/I can’t afford to go',
                'I did not find the experience or information helpful',
                'I did not like the customer experience at the office',
                'I am afraid to go for fear of what I might find out',
                'I just don’t like doctor’s offices or hospitals',
                'Other'
              ]
            }
          }
        ]
      },
      {
        stepId: 'medical_step_1',
        completed: false,
        questions: [
          {
            type: 'RadioGrid',
            questionId: 'medical_HNpLvC',
            details: {
              page: 4,
              question:
                'Please fill out the below chart regarding your current and past medical history.',
              xLabels: [
                'Currently being treated for or active',
                'In the past & resolved',
                'Never',
                'N/A (for gender-specific questions)'
              ],
              yLabels: [
                'Atrial Fibrillation',
                'Heart Valve Issues',
                'Other Heart Issues',
                'Anemia (Low Blood Count)',
                'Immunosuppressed – caused by medications',
                'High Blood Pressure',
                'High Cholesterol',
                'Type 1 Diabetes',
                'Type 2 Diabetes',
                'Fatty Liver',
                'Kidney Disease',
                'Pancreatitis',
                'Crohn’s or Ulcerative Colitis',
                'Irritable Bowel Syndrome'
              ]
            }
          }
        ]
      },
      {
        stepId: 'medical_step_2',
        completed: false,
        questions: [
          {
            type: 'RadioGrid',
            questionId: 'medical_PNXlvZ',
            details: {
              page: 4,
              question:
                'Please fill out the below chart regarding your current and past medical history.',
              xLabels: [
                'Currently being treated for or active',
                'In the past & resolved',
                'Never',
                'N/A (for gender-specific questions)'
              ],
              yLabels: [
                'Celiac Disease',
                'Non Celiac Gluten intolerance',
                'Acid Reflux',
                'Gastric or esophageal ulcers',
                'Regular constipation',
                'Regular diarrhea',
                'Autoimmune conditions (e.g. Rheumatoid Arthritis, Lupus, Psoriasis)',
                'Hypo or Hyperthyroidism',
                'Depression',
                'Anxiety',
                'Bipolar disease or schizophrenia',
                'Gestational Diabetes',
                'Erectile Dysfunction',
                'Infertility',
                'PCOS (Polycystic Ovary Syndrome)',
                'Menopause'
              ]
            }
          }
        ]
      },
      {
        stepId: 'medical_step_3',
        completed: false,
        questions: [
          {
            type: 'RadioGrid',
            questionId: 'medical_QolwvB',
            details: {
              page: 4,
              question: 'Have you ever been treated for the following cancers:',
              xLabels: [
                'Currently being treated for or active',
                'In the past & resolved',
                'Never',
                'N/A (for gender-specific questions)'
              ],
              yLabels: [
                'Brain',
                'Thyroid',
                'Breast',
                'Colon/Anal',
                'Lung',
                'Kidney',
                'Pancreatic',
                'Liver',
                'Gastric',
                'Skin',
                'Bladder',
                'Prostate',
                'Testicular',
                'Ovarian',
                'Uterine',
                'Cervical',
                'Leukemia of Lymphoma',
                'Multiple Myeloma',
                'Other'
              ]
            }
          }
        ]
      },
      {
        stepId: 'medical_step_4',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'medical_Xz123M',
            details: {
              page: 5,
              question:
                'Any other medical conditions not covered in the previous tables?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: {
                  hide: [
                    'medication_s1LCr5',
                    'medical_EzSENQ',
                    'medical_mVRtHm',
                    'medical_GmGS4U'
                  ]
                },
                Yes: {
                  show: [
                    'medication_s1LCr5',
                    'medical_EzSENQ',
                    'medical_mVRtHm',
                    'medical_GmGS4U'
                  ]
                }
              }
            }
          },
          {
            type: 'SingleText',
            questionId: 'medication_s1LCr5',
            // required: true,
            display: false,
            details: {
              question: 'Other Medical Conditions',
              text: {
                size: 'tiny'
              }
            }
          },
          {
            type: 'RadioGrid',
            questionId: 'medical_EzSENQ',
            display: false,
            details: {
              page: 4,
              question:
                'Please fill out the below chart regarding your current and past medical history.',
              xLabels: [
                'Diagnosed less than 1 year ago',
                'Diagnosed 1-5 years ago',
                'Diagnosed 6-10 years ago',
                'Diagnosed >10 years ago'
              ],
              yLabels: [
                'Adult Acne',
                'Hair Loss',
                'Asthma',
                'COPD',
                'Seasonal Allergies',
                'Ever had Covid',
                'Stroke',
                'Parkinson’s Disease',
                'Alzheimer’s Disease',
                'Multiple Sclerosis',
                'Other Dementia',
                'Heart Attack',
                'Atrial Fibrillation',
                'Heart Valve Issues',
                'Other Heart Issues',
                'Anemia (low blood count)'
              ],
              noneOfAboveText: 'Check here if none of the above apply.'
            }
          },
          {
            type: 'RadioGrid',
            questionId: 'medical_mVRtHm',
            display: false,
            details: {
              page: 4,
              question:
                'Please fill out the below chart regarding your current and past medical history.',
              xLabels: [
                'Diagnosed less than 1 year ago',
                'Diagnosed 1-5 years ago',
                'Diagnosed 6-10 years ago',
                'Diagnosed >10 years ago'
              ],
              yLabels: [
                'Immunosuppressed – caused by medications',
                'High Blood Pressure',
                'High Cholesterol',
                'Type 1 Diabetes',
                'Type 2 Diabetes',
                'Diabetic Retinopathy',
                'Diabetic Kidney Disease',
                'Diabetic Neuropathy',
                'Fatty Liver',
                'All other non-diabetic kidney disease',
                'Pancreatitis',
                'Crohn’s or Ulcerative Colitis',
                'Irritable Bowel Syndrome',
                'Celiac Disease',
                'Non Celiac Gluten intolerance',
                'Acid Reflux'
              ],
              noneOfAboveText: 'Check here if none of the above apply.'
            }
          },
          {
            type: 'RadioGrid',
            questionId: 'medical_GmGS4U',
            display: false,
            details: {
              page: 4,
              question:
                'Please fill out the below chart regarding your current and past medical history.',
              xLabels: [
                'Diagnosed less than 1 year ago',
                'Diagnosed 1-5 years ago',
                'Diagnosed 6-10 years ago',
                'Diagnosed >10 years ago'
              ],
              yLabels: [
                'Gastric or Esophageal Ulcers',
                'Regular Constipation',
                'Regular Diarrhea',
                'Autoimmune conditions (e.g. Rheumatoid Arthritis, Lupus, Psoriasis)',
                'Hypo or Hyperthyroidism',
                'Depression',
                'Anxiety',
                'Bipolar Disease or Schizophrenia',
                'Gestational Diabetes',
                'Erectile Dysfunction',
                'Infertility',
                'PCOS (Polycystic Ovary Syndrome)',
                'Menopause'
              ],
              noneOfAboveText: 'Check here if none of the above apply.'
            }
          }
        ]
      },
      {
        stepId: 'medical_step_5',
        completed: false,
        questions: [
          {
            type: 'Checkbox',
            questionId: 'medical_gae3ca',
            details: {
              columns: true,
              question:
                'Please choose all major procedures you have undergone:',
              options: [
                'Amputation',
                'Heart Bypass Surgery',
                'Heart Valve Replacement',
                'Pacemaker or Defibrillator',
                'Hip Replacement',
                'Knee Replacement',
                'Any type of back surgery',
                'Other Orthopedic Procedures',
                'Plastic Surgeries',
                'Hysterectomy',
                'Removal of Ovaries',
                'Colon Resection',
                'Appendix Removal',
                'Gastric Bypass or other procedures for weight loss',
                'Gall Bladder Removal',
                'Other surgeries on your GI system',
                'N/A',
                'Other'
              ],
              showOthers: 'Other',
              text: {
                size: 'medium',
                horizontal: true,
                width: '50%',
                required: true
              }
            }
          }
        ]
      },
      {
        stepId: 'medical_step_6',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'medical_6EL1gI',
            details: {
              page: 5,
              question: 'What is your top reason for visiting the Thorne Lab?',
              options: [
                'I feel great but want to learn more about my body and continue to refine my habits.',
                'I think I am at higher risk for health issues and want to better understand my risk profile.',
                'I am having some health or lifestyle issues which I would like to address.',
                'No specific reason - just curious.'
              ],
              otherOption: 'true',
              checkbox: {
                radioOption:
                  'I am having some health or lifestyle issues which I would like to address.',
                options: [
                  'Poor Sleep',
                  'Weight Loss',
                  'Improve Body Composition',
                  'Anxiety/Depression',
                  'Improve my physical activity/exercise regimen',
                  'Optimize my nutrition/diet plan',
                  '“Brain Fog”',
                  'Low Energy'
                ]
              }
            }
          }
        ]
      },
      {
        stepId: 'medical_step_7',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'medical_utSb7S',
            details: {
              page: 5,
              horizontal: true,
              question:
                'Do you currently or have you ever been seen by a primary care physician? ',
              options: ['Yes', 'No'],
              routeMap: {
                No: {
                  show: ['medical_Ove78g']
                },
                Yes: {
                  hide: ['medical_Ove78g', 'medical_98NJ0Q']
                }
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'medical_Ove78g',
            display: false,
            details: {
              page: 5,
              horizontal: true,
              question:
                'Have you ever been seen by a primary care physician as an adult?',
              options: ['Yes', 'No'],
              routeMap: {
                No: {
                  show: ['medical_98NJ0Q']
                },
                Yes: {
                  hide: ['medical_98NJ0Q']
                }
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'medical_98NJ0Q',
            display: false,
            details: {
              page: 5,
              question: 'Your reason for not having a primary care physician',
              options: [
                'Can’t find one near me or can’t get a convenient appointment',
                'Don’t have insurance/good insurance/I can’t afford to go',
                'I did not find the experience or information helpful',
                'I did not like the customer experience at the office',
                'I am afraid to go for fear of what I might find out',
                'I just don’t like doctor’s offices or hospitals',
                'N/A'
              ],
              otherOption: 'true',
              text: {
                size: 'medium',
                horizontal: true,
                width: '18%'
              }
            }
          }
        ]
      },
      {
        stepId: 'medical_step_8',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'medical_cwyMu8',
            details: {
              page: 5,
              question: 'How long ago did you have your last visit?',
              options: [
                'Within the past year',
                'Between 1-5 years',
                '>5 years'
              ],
              routeMap: {
                'Within the past year': {
                  hide: ['medical_98NJ0E']
                },
                'Between 1-5 years': {
                  hide: ['medical_98NJ0E']
                },
                '>5 years': {
                  show: ['medical_98NJ0E']
                }
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'medical_98NJ0E',
            display: false,
            details: {
              page: 5,
              question: 'Your reason for not having a primary care physician',
              options: [
                'Can’t find one near me or can’t get a convenient appointment',
                'Don’t have insurance/good insurance/I can’t afford to go',
                'I did not find the experience or information helpful',
                'I did not like the customer experience at the office',
                'I am afraid to go for fear of what I might find out',
                'I just don’t like doctor’s offices or hospitals',
                'N/A'
              ],
              otherOption: 'true',
              text: {
                size: 'medium',
                horizontal: true,
                width: '18%'
              }
            }
          }
        ]
      }
    ],
    questions: [],
    answers: {
      example: {
        chiefComplaint: {
          symptom: {
            description: 'Back ache',
            severity: 3,
            duration: '0-4 weeks',
            medication: 'Yes'
          },
          activity: {
            description: 'Playing tennis',
            severity: 3
          },
          wellBeing: 4
        },
        medicalHistory: {
          present: [{ issue: 'Soar arm', yearOnset: '1967' }],
          past: [{ issue: 'Soar toe', yearResolution: '1968' }],
          surgery: [{ issue: 'Soar foot', year: '1950' }]
        }
      },
      raw: [],
      parsed: {}
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  medications: {
    title: 'Allergies and Medications',
    route: '/me/medications',
    shortName: 'medications',
    completed: false,
    order: 4,
    timeToComplete: '2min',
    _meta: {
      route: '/landing/assessments/medications'
    },
    steps: [
      {
        stepId: 'medications_step_0',
        completed: false,
        questions: [
          {
            type: 'Checkbox',
            questionId: 'medication_SDyPGo',
            details: {
              question: 'Please choose all items you are allergic to:',
              options: [
                'Penicillin',
                'Sulfa antibiotics',
                'NSAIDs (e.g. Ibuprofen, Aleve, etc.)',
                'Iodine dye (e.g. used for Cat Scans)',
                'Latex',
                'Shellfish',
                'Nuts',
                'Soy',
                'Wheat',
                'Dairy',
                'N/A',
                'Other'
              ],
              showOthers: 'Other',
              text: {
                size: 'medium',
                horizontal: true,
                width: '18%',
                required: true
              }
            }
          }
        ]
      },
      {
        stepId: 'medications_step_1',
        completed: false,
        questions: [
          {
            type: 'FilterList',
            questionId: 'medication_s1BxU1',
            // required: true,
            isOptAnswered: false,
            details: {
              columns: true,
              page: 3,
              question:
                'Please select all prescription medications you are currently taking. (Use the scroll bar to browse possible choices and/or type the first letter to find a drug. If a drug is not listed, please enter it in the "Other Medications " find below.)',
              label: 'Search Medications',
              size: 'large',
              searchable: true,
              placeholder: 'None',
              stretchedDropdown: true,
              addMoreButton: {
                message: 'Add Another Medication'
              },
              medicationList: medications
            }
          },
          {
            type: 'SingleText',
            questionId: 'medication_s1LCr5',
            isOptAnswered: false,
            // required: true,
            display: true,
            details: {
              question: 'Other Medications',
              text: {
                size: 'tiny'
              }
            }
          },
          {
            type: 'Checkbox',
            questionId: 'medication_s154OE',
            display: true,
            details: {
              question: '',
              fontSize: '18px',
              options: ['Check here if none of the above apply.']
            }
          }
        ]
      },
      {
        stepId: 'medications_step_2',
        completed: false,
        questions: [
          {
            type: 'Checkbox',
            questionId: 'medication_SkkFXE',
            details: {
              question:
                'Please choose all non-prescription drugs or supplements you are taking:',
              options: [
                'Pain relievers',
                'Cold and Allergy',
                'Magnesium',
                'Multi-vitamin',
                'Probiotics',
                'Omega-3/Fish oil',
                'Calcium',
                'Vitamin D',
                'Melatonin',
                'Other sleep aids',
                'N/A',
                'Other'
              ],
              showOthers: 'Other',
              text: {
                size: 'medium',
                horizontal: true,
                width: '18%',
                required: true
              }
            }
          }
        ]
      }
    ],
    questions: [
      {
        type: 'TextDropdown',
        details: {
          page: 0,
          notRequired: true,

          question:
            'Please list prescription and over-the-counter medications you are currently taking. Please include vitamins, supplements and herbal remedies.',
          text: { size: 'medium', label: 'Name' },
          dropdown: {
            label: 'Frequency',
            size: 'medium',
            options: [
              'Once Daily',
              'Twice Daily',
              'Three Times Daily',
              'Four Times Daily',
              'As Needed'
            ]
          },
          addMoreButton: {
            message: 'Add Another Medication'
          }
        }
      },
      {
        type: 'DoubleText',
        details: {
          page: 1,
          notRequired: true,

          question:
            'Please list any allergies you have to medications, foods, or other.',
          text1: { size: 'medium', label: 'Allergen' },
          addMoreButton: {
            message: 'Add Another Allergy'
          },
          text2: { size: 'medium', label: 'Reaction' }
        }
      }
    ],
    answers: {
      example: {
        medications: [{ name: 'Amphetamine', frequency: 'Once daily' }],
        allergies: [{ allergen: 'Dust', reaction: 'Sneezing' }]
      },
      raw: [],
      parsed: {
        allergies: {
          questionId: 'medication_SDyPGo',
          answerKey: 'selected'
        },
        medications: [
          { questionId: 'medication_s1BxU1', answerKey: 'selected' },
          { questionId: 'medication_s1LCr5', answerKey: 'value' },
          { questionId: 'medication_SkkFXE', answerKey: 'selected' }
        ]
      }
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  sleep: {
    title: 'Sleep Quality',
    route: '/me/assessment/sleep',
    shortName: 'sleep',
    completed: false,
    order: 5,
    timeToComplete: '2min',
    _meta: {
      route: '/landing/assessments/sleep'
    },
    steps: [
      {
        stepId: 'sleep_step_0',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              notRequired: true,
              page: 0,
              title: {
                text: 'During the past month…'
              }
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'sleep_6OxKpn',
            details: {
              page: 0,
              notRequired: true,
              picker: true,
              text: { size: 'tiny', hours: '', minutes: '' },
              validation: {
                type: 'difference',
                questionId: 'sleep_f9laFO'
              },
              question: 'When have you usually gone to bed?'
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'sleep_aIcVmt',
            details: {
              page: 0,
              notRequired: true,
              onlyMinutes: true,
              text: { size: 'tiny', hours: '', minutes: 'minutes' },
              question:
                'How long (in minutes) has it taken you to fall asleep each night?'
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'sleep_f9laFO',
            details: {
              page: 0,
              notRequired: true,
              picker: true,
              text: { size: 'tiny', hours: '', minutes: '' },
              validation: {
                type: 'difference',
                questionId: 'sleep_6OxKpn'
              },
              question: 'When have you usually gotten up in the morning?'
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'sleep_A6sayb',
            details: {
              page: 0,
              notRequired: true,
              onlyHours: true,
              text: { size: 'tiny', hours: 'hours', minutes: '' },
              validation: {
                type: '>0'
              },
              question:
                'How many hours of actual sleep do you get at night? (This may be different than the number of hours you spend in bed)'
            }
          }
        ]
      },
      {
        stepId: 'sleep_step_1',
        completed: false,
        questions: [
          {
            type: 'RadioGrid',
            questionId: 'sleep_CIgKyO',
            details: {
              page: 4,
              question:
                'During the past month, how often have you had trouble sleeping because you...',
              xLabels: [
                'Not during the past month',
                'Less than once a week',
                'Once or twice a week',
                'Three or more times a week'
              ],
              yLabels: [
                'Cannot get to sleep within 30 minutes',
                'Wake up in the middle of the night or early morning',
                'Have to get up to use the bathroom',
                'Cannot breathe comfortably',
                'Cough or snore loudly',
                'Feel too cold',
                'Feel too hot',
                'Have bad dreams',
                'Have pain',
                'Other reason(s)'
              ]
            }
          },
          {
            type: 'Radio',
            questionId: 'sleep_UaN1DC',
            details: {
              page: 5,
              searchable: false,
              question:
                'During the past month, how would you rate your sleep quality overall?',
              options: ['Very good', 'Fairly good', 'Fairly bad', 'Very bad'],
              fullWidth: true
            }
          },
          {
            type: 'RadioGrid',
            questionId: 'sleep_CIxTyL',
            details: {
              page: 4,
              question: 'During the past month, how often have you...',
              xLabels: [
                'Not during the past month',
                'Less than once a week',
                'Once or twice a week',
                'Three or more times a week'
              ],
              yLabels: [
                'Taken medicine (prescribed or “over the counter”) to help you sleep?',
                'Had trouble staying awake while driving, eating meals, or engaging in social activity?',
                'Had a problem keeping up your enthusiasm to get things done?'
              ]
            }
          }
        ]
      }
    ],
    questions: [],
    answers: {
      example: {
        bedTime: '23:30:00',
        minutesToSleep: 15,
        outOfBedTime: '6:30:00',
        durationOfSleep: 7,
        sleepInterruption: {
          hardToSleep: 'Not during the past month',
          wakeUpDuringSleep: 'Less than once a week',
          useTheBathroomDuringSleep: 'Not during the past month',
          breatheUncomfortably: 'Once or twice a week',
          coughOrSnore: 'Three or more times a week',
          tooCold: 'Three or more times a week',
          tooHot: 'Once or twice a week',
          badDreams: 'Once or twice a week',
          pain: 'Once or twice a week',
          otherReasons: 'Not during the past month'
        },
        sleepQuality: 'Very good',
        sleepMedication: 'Not during the past month',
        stayingAwake: 'Not during the past month',
        enthusiasm: 'Once or twice a week'
      },
      raw: [],
      parsed: {
        bedTime: {
          questionId: 'sleep_6OxKpn',
          answerKey: 'time'
        },
        minutesToSleep: {
          questionId: 'sleep_aIcVmt',
          answerKey: 'minutes'
        },
        outOfBedTime: {
          questionId: 'sleep_f9laFO',
          answerKey: 'time'
        },
        durationOfSleep: {
          questionId: 'sleep_A6sayb',
          answerKey: 'hours'
        },
        sleepInterruption: {
          nestedObject: true,
          hardToSleep: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Cannot get to sleep within 30 minutes'
          },
          wakeUpDuringSleep: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Wake up in the middle of the night or early morning'
          },
          useTheBathroomDuringSleep: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Have to get up to use the bathroom'
          },
          breatheUncomfortably: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Cannot breathe comfortably'
          },
          coughOrSnore: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Cough or snore loudly'
          },
          tooCold: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Feel too cold'
          },
          tooHot: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Feel too hot'
          },
          badDreams: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Have bad dreams'
          },
          pain: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Have pain'
          },
          otherReasons: {
            questionId: 'sleep_CIgKyO',
            answerKey: 'Other reason(s)'
          }
        },
        sleepQuality: {
          questionId: 'sleep_UaN1DC',
          answerKey: 'selected'
        },
        sleepMedication: {
          questionId: 'sleep_CIxTyL',
          answerKey:
            'Taken medicine (prescribed or “over the counter”) to help you sleep?'
        },
        stayingAwake: {
          questionId: 'sleep_CIxTyL',
          answerKey:
            'Had trouble staying awake while driving, eating meals, or engaging in social activity?'
        },
        enthusiasm: {
          questionId: 'sleep_CIxTyL',
          answerKey:
            'Had a problem keeping up your enthusiasm to get things done?'
        }
      }
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  physicalActivity: {
    title: 'Physical Activity',
    route: '/me/assessment/physical-activity',
    shortName: 'physicalActivity',
    completed: false,
    order: 6,
    timeToComplete: '5min',
    width: '72%',
    _meta: {
      route: '/landing/assessments/physicalActivity'
    },
    steps: [
      {
        stepId: 'physicalactivity_step_0',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              notRequired: true,
              page: 0,
              title: {
                text: 'International Physical Activity Questionnaire'
              },
              message:
                'We are interested in finding out about the kinds of physical activities that people do as part of their everyday lives. The questions will ask you about the time you spent being physically active in the last 7 days. Please answer each question even if you do not consider yourself to be an active person. Please think about the activities you do at work, as part of your house and yard work, to get from place to place, and in your spare time for recreation, exercise, or sport. <br/><br/>Think about all the vigorous activities that you did in the last 7 days. Vigorous physical activities refer to activities that take hard physical effort and make you breathe much harder than normal. Think only about those physical activities that you did for at least 10 minutes at a time.'
            }
          },
          {
            type: 'Dropdown',
            questionId: 'physicalactivity_6P00PV',
            details: {
              page: 0,
              question:
                'During the last 7 days, on how many days did you do vigorous physical activities like heavy lifting, digging, aerobics, or fast bicycling?',
              label: 'Days',
              placeholder: 'Select Number',
              size: 'small',
              searchable: false,
              options: ['0', '1', '2', '3', '4', '5', '6', '7']
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'physicalactivity_E4QYIV',
            details: {
              page: 1,
              question:
                'How much time do you usually spend doing vigorous physical activities on one of those days?',
              text: { size: 'tiny', hours: 'hours', minutes: 'minutes' }
            }
          }
        ]
      },
      {
        stepId: 'physicalactivity_step_1',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              notRequired: true,
              page: 0,
              message:
                'Think about all the moderate activities that you did in the last 7 days. Moderate activities refer to activities that take moderate physical effort and make you breathe somewhat harder than normal. Think only about those physical activities that you did for at least 10 minutes at a time.'
            }
          },
          {
            type: 'Dropdown',
            questionId: 'physicalactivity_tAkhqH',
            details: {
              page: 2,
              question:
                'During the last 7 days, on how many days did you do moderate physical activities like carrying light loads, bicycling at a regular pace, or doubles tennis? Do not include walking.',
              label: 'Days',
              placeholder: 'Select Number',
              size: 'small',
              searchable: false,
              options: ['0', '1', '2', '3', '4', '5', '6', '7']
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'physicalactivity_j9Wkvd',
            details: {
              page: 3,
              question:
                'How much time do you usually spend doing moderate physical activities on one of those days?',
              text: { size: 'tiny', hours: 'hours', minutes: 'minutes' }
            }
          }
        ]
      },
      {
        stepId: 'physicalactivity_step_2',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              notRequired: true,
              page: 0,
              message:
                'Think about the time you spent walking in the last 7 days. This includes at work and at home, walking to travel from place to place, and any other walking that you have done solely for recreation, sport, exercise, or leisure.'
            }
          },
          {
            type: 'Dropdown',
            questionId: 'physicalactivity_go46VK',
            details: {
              page: 4,
              question:
                'During the last 7 days, on how many days did you walk for at least 10 minutes at a time?',
              label: 'Days',
              size: 'small',
              placeholder: 'Select Number',
              searchable: false,
              options: ['0', '1', '2', '3', '4', '5', '6', '7']
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'physicalactivity_rdPDML',
            details: {
              page: 5,
              question:
                'How much time do you usually spend walking on one of those days?',
              text: { size: 'tiny', hours: 'hours', minutes: 'minutes' }
            }
          }
        ]
      },
      {
        stepId: 'physicalactivity_step_3',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              notRequired: true,
              page: 0,
              message:
                'The last question is about the time you spent sitting on weekdays during the last 7 days. Include time spent at work, at home, while doing course work and during leisure time. This may include time spent sitting at a desk, visiting friends, reading, or sitting or lying down to watch television.'
            }
          },
          {
            type: 'TimeEditor',
            questionId: 'physicalactivity_H2EdRn',
            details: {
              page: 6,
              question:
                'During the last 7 days, how much time did you spend sitting on a weekday?',
              text: { size: 'tiny', hours: 'hours', minutes: 'minutes' }
            }
          }
        ]
      }
    ],
    answers: {
      example: {
        vigorousDays: 2,
        vigorousHours: 4,
        vigorousMins: 32,
        moderateDays: 5,
        moderateHours: 3,
        moderateMins: 15,
        walkingDays: 7,
        walkingHours: 3,
        walkingMins: 0,
        sitHours: 10,
        sitMins: 0
      },
      raw: [],
      parsed: {
        vigorousDays: {
          questionId: 'physicalactivity_6P00PV',
          answerKey: 'formItems.0.selected'
        },
        vigorousHours: {
          questionId: 'physicalactivity_E4QYIV',
          answerKey: 'hours'
        },
        vigorousMins: {
          questionId: 'physicalactivity_E4QYIV',
          answerKey: 'minutes'
        },
        moderateDays: {
          questionId: 'physicalactivity_tAkhqH',
          answerKey: 'formItems.0.selected'
        },
        moderateHours: {
          questionId: 'physicalactivity_j9Wkvd',
          answerKey: 'hours'
        },
        moderateMins: {
          questionId: 'physicalactivity_j9Wkvd',
          answerKey: 'minutes'
        },
        walkingDays: {
          questionId: 'physicalactivity_go46VK',
          answerKey: 'formItems.0.selected'
        },
        walkingHours: {
          questionId: 'physicalactivity_rdPDML',
          answerKey: 'hours'
        },
        walkingMins: {
          questionId: 'physicalactivity_rdPDML',
          answerKey: 'minutes'
        },
        sitHours: { questionId: 'physicalactivity_H2EdRn', answerKey: 'hours' },
        sitMins: { questionId: 'physicalactivity_H2EdRn', answerKey: 'minutes' }
      }
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  nutrition: {
    title: 'Nutrition',
    route: '/me/assessment/nutrition',
    shortName: 'nutrition',
    completed: false,
    order: 7,
    timeToComplete: '5min',
    width: '72%',
    _meta: {
      route: '/landing/assessments/nutrition'
    },
    steps: [
      {
        stepId: 'nutrition_step_0',
        completed: false,
        questions: [
          {
            type: 'Message',
            required: false,
            details: {
              notRequired: true,
              page: 0,
              title: {
                text: 'National Health and Nutritional Examination Survey'
              },
              message:
                'These questions are about the different kinds of foods you ate or drank during the past month, that is, the past 30 days. When answering, please include meals and snacks eaten at home, at work or school, in restaurants, and anyplace else.'
            }
          },
          {
            type: 'Radio',
            questionId: 'nutrition_JQ4bZp',
            details: {
              page: 1,
              question:
                'During the past month, did you eat any <span class="medium-weight">hot or cold cereals</span>?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: {
                  hide: [
                    'nutrition_mOwz3p',
                    'nutrition_kjBxU1',
                    'nutrition_J84bZX',
                    'nutrition_kTMqU4'
                  ]
                },
                Yes: {
                  show: [
                    'nutrition_mOwz3p',
                    'nutrition_kjBxU1',
                    'nutrition_J84bZX'
                  ]
                }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_mOwz3p',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat hot or cold cereals? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                max: '99',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          },
          {
            type: 'Dropdown',
            questionId: 'nutrition_kjBxU1',
            customClassOther: 'survey-item-large',
            display: false,
            details: {
              page: 3,
              question:
                'During the past month, what kind of cereal did you usually eat? Pick one. \n (Use the scroll bar to browse possible choices and/or type the first letter to find your cereal. If your cereal is not listed, please select Other at the end of the list and then enter the cereal name.)',
              label: 'Search cereals',
              size: 'full',
              searchable: true,
              placeholder: 'Select Cereals',
              // addMoreButton: {
              //   message: 'Add Another Cereal'
              // },
              options: [
                '100% Bran',
                '100% Low Fat Natural Granola',
                '100% Natural Cereal',
                '100% Natural Cereal, with oats, honey and raisins',
                '100% Natural Granola, Oats & Honey',
                '100% Natural Wholegrain Cereal with raisins, low fat',
                'All-Bran',
                'All-Bran Bran Buds',
                'All-Bran with Extra Fiber',
                'Alpen',
                'Alpha-Bits',
                'Alpha-Bits with marshmallows',
                'Amaranth Flakes',
                'Apple Jacks',
                'Apple Zaps',
                'Apple Zings, Malt-O-Meal',
                'Banana Nut Crunch Cereal',
                'Barley',
                'Basic 4',
                'Berry Colossal Crunch, Malt-O-Meal',
                'Blueberry Morning',
                'Booberry',
                'Bran',
                'Bran Buds',
                'Bran flakes',
                'Bran, Nabisco',
                'Branola',
                'Brown Sugar Bliss',
                'Buckwheat groats',
                'Bulgur',
                "Cap'n Crunch",
                "Cap'n Crunch's Christmas Crunch",
                "Cap'n Crunch's Crunch Berries",
                "Cap'n Crunch's Oops! ChocoDonuts",
                "Cap'n Crunch's Peanut Butter Crunch",
                'Cheerios',
                'Cheerios, Apple Cinnamon',
                'Cheerios, Berry Burst',
                'Cheerios, Berry Burst Strawberry',
                'Cheerios, Berry Burst Triple Berry',
                'Cheerios, Berry Burst, Cherry Vanilla',
                'Cheerios, Berry Burst, Strawberry Banana',
                'Cheerios, Frosted',
                'Cheerios, Honey Nut',
                'Cheerios, Multi Grain',
                'Cheerios, Team',
                'Cheerios, Yogurt Burst, Strawberry',
                'Cheerios, Yogurt Burst, Vanilla',
                'Cheese grits',
                'Chex',
                'Chex Morning Mix Banana Nut',
                'Chex Morning Mix Cinnamon',
                'Chex Morning Mix Fruit & Nut',
                'Chex Morning Mix Honey Nut',
                'Chex, Bran',
                'Chex, Corn',
                'Chex, Honey Nut',
                'Chex, Multi-Bran',
                'Chex, Rice',
                'Chex, Wheat',
                'Chocolate frosted cereal',
                'Cinnamon Cluster Raisin Bran',
                'Cinnamon Crunch Crispix',
                'Cinnamon Grahams Cereal',
                'Cinnamon Marshmallow Scooby Doo!',
                'Cinnamon Toast Crunch',
                'Cinnamon Toast Crunch, Reduced Sugar',
                'Coco-Roos, Malt-O-Meal',
                'Cocoa Blasts',
                'Cocoa Comets',
                'Cocoa Dyno Bites, Malt-O-Meal',
                'Cocoa Krispies',
                'Cocoa Pebbles',
                'Cocoa Puffs',
                'Cocoa Puffs, Reduced Sugar',
                'Cocoa Wheats',
                'Complete Bran Flakes',
                'Complete Oat Bran Flakes',
                'Complete Wheat Bran Flakes',
                'Cookie-Crisp (all flavors)',
                'Corn Bursts, Malt-O-Meal',
                "Corn Flakes, Kellogg's",
                'Corn Pops',
                'Corn Puffs',
                'Corn flakes',
                'Corn flakes, low sodium',
                'Cornmeal mush',
                'Count Chocula',
                "Cracklin' Oat Bran",
                'Cranberry Almond Crunch Cereal',
                'Cream of Rice',
                'Cream of Rye',
                'Cream of Wheat',
                'Crisp Crunch',
                'Crispix',
                'Crispy Brown Rice Cereal',
                'Crispy Rice',
                'Crispy Rice, Malt-O-Meal',
                "Crispy Wheats'N Raisins",
                'Crunchy Corn Bran',
                'Disney Cereal',
                "Disney Hunny B's",
                "Disney Mickey's Magix",
                'Disney Mud & Bugs',
                'Ener-G Pure Rice Bran',
                'Familia',
                'Farina',
                'Fiber 7 Flakes',
                'Fiber One',
                'Frankenberry',
                'French Toast Crunch',
                'Froot Loops',
                "Frosted Flakes, Kellogg's",
                'Frosted Flakes, Malt-O-Meal',
                'Frosted Fruit Rings',
                'Frosted Mini Spooners, Malt-O-Meal',
                'Frosted Mini Wheats',
                'Frosted Shredded Wheat',
                'Frosted Wheat Bites',
                'Frosted cereal, with marshmallows',
                'Frosted corn flakes',
                'Frosted flakes',
                'Frosted rice',
                "Frosty O's",
                'Fruit & Fibre (fiber)',
                'Fruit & Fibre (fiber) with Dates, Raisins and Walnuts',
                'Fruit & Fibre (fiber) with Peaches, Raisins, Almonds, and Oat Clusters',
                'Fruit Harvest',
                'Fruit Harvest Apple Cinnamon',
                'Fruit Harvest Strawberry Blueberry',
                'Fruit Loops',
                'Fruit Rings',
                'Fruit Whirls',
                'Fruit and Cream Oatmeal',
                'Fruity Dyno Bites, Malt-O-Meal',
                'Fruity Pebbles',
                'Golden Crisp',
                'Golden Grahams',
                'Golden Puffs, Malt-O-Meal',
                'Granola',
                'Granola, homemade',
                'Granola, lowfat',
                "Granola, lowfat, Kellogg's",
                "Granola, lowfat, with Raisins, Kellogg's",
                "Grape Nut O's",
                'Grape-Nuts',
                'Grape-Nuts Flakes',
                'Great Grains Crunchy Pecan Whole Grain Cereal',
                'Great Grains, Raisins, Dates, and Pecans Whole Grain Cereal',
                'Grits',
                'Harina de maize con leche',
                'Harmony Vanilla Almond Oats',
                'Healthy Choice',
                'Honey Bunches of Oat Honey Roasted',
                'Honey Bunches of Oat with Strawberry',
                'Honey Bunches of Oats',
                'Honey Bunches of Oats with Almonds',
                'Honey Buzzers, Malt-O-Meal',
                'Honey Crisp Corn Flakes',
                'Honey Crunch Corn Flakes',
                'Honey Graham Squares, Malt-O-Meal',
                'Honey Nut Clusters',
                'Honey Nut Heaven',
                'Honey Nut Shredded Wheat',
                'Honey Smacks',
                'Honeycomb',
                'Honeycomb, strawberry',
                'Instant Grits, all flavors',
                "Jenny O's",
                'Just Right',
                'Just Right with Fruit & Nut',
                'Kaboom',
                'Kasha',
                'Kashi',
                'Kashi GOLEAN',
                'Kashi Good Friends',
                'Kashi Good Friends Cinna-Raisin Crunch',
                'Kashi Heart to Heart Cereal',
                'Kashi Honey Puffed',
                'Kashi Medley',
                'Kashi Organic Promise',
                'Kashi Pilaf',
                'Kashi Pillows',
                'Kashi Seven in the Morning',
                'Kashi, Puffed',
                'Kix',
                'Kix, Berry Berry',
                'Life (plain and cinnamon)',
                'Lucky Charms',
                'Lucky Charms, Berry',
                'Lucky Charms, Chocolate',
                'Magic Stars',
                'Malt-O-Meal',
                'Malt-O-Meal, chocolate',
                'Maltex',
                'Marshmallow Mateys, Malt-O-Meal',
                'Marshmallow Safari',
                'Masa harina',
                'Maypo',
                'Millet',
                'Millet, puffed',
                'Mini-Wheats',
                'Mini-Wheats Frosted Bite Size',
                'Mini-Wheats Frosted Original',
                'Mini-Wheats Frosted Raisin',
                'Mini-Wheats Frosted Strawberry',
                "Mother's Natural Foods Cereal, Quaker",
                'Muesli',
                'Muesli(x)',
                'Multigrain Oatmeal',
                'Multigrain cereal',
                'Natural Bran Flakes',
                'Nature Valley Granola',
                'Nature Valley Granola, with fruit and nuts',
                'Nesquik',
                'Nestum',
                'Nu System Cuisine Toasted Grain Circles',
                'Nutri-Grain',
                'Nutri-Grain Golden Wheat and Raisin',
                'Nutty Nuggets',
                'Oat Bran Cereal, Quaker',
                'Oat Bran Flakes, Health Valley',
                'Oat bran cereal',
                'Oat bran uncooked',
                'Oat cereal',
                'Oat flakes',
                'Oatmeal',
                'Oatmeal Crisp',
                'Oatmeal Crisp with Almonds',
                'Oatmeal Crisp, Apple Cinnamon',
                'Oatmeal Crisp, Raisin',
                'Oatmeal Squares',
                'Oatmeal Swirlers',
                'Oats, raw',
                "Oh's",
                "Oh's, Apple Cinnamon",
                "Oh's, Fruitangy",
                "Oh's, Honey Graham",
                'Old Wessex Irish Style Oatmeal',
                "Optimum Slim, Nature's Path",
                "Optimum, Nature's Path",
                "Oreo O's Cereal",
                'Peanut Butter Toast Crunch',
                'Polenta',
                'Product 19',
                'Puffed Rice, Malt-O-Meal',
                'Puffed Wheat, Malt-O-Meal',
                'Quaker Dinosaur Eggs oatmeal',
                'Quaker Fruit and Cream Oatmeal',
                'Quaker Instant Grits, all flavors',
                'Quaker Multigrain Oatmeal',
                'Quaker Oatmeal Express',
                'Quaker Oatmeal Nutrition for Women',
                'Quaker Oatmeal Squares',
                'Quisp',
                'Raisin Bran Crunch',
                "Raisin Bran, Kellogg's",
                'Raisin Bran, Post',
                'Raisin Nut Bran',
                'Raisin bran (generic)',
                "Reese's Peanut Butter Puffs",
                'Rice Krispies',
                'Rice Krispies, Frosted',
                'Rice Krispies, Treats Cereal',
                'Rice bran, uncooked',
                'Rice cereal',
                'Rice flakes',
                'Rice polishings',
                'Rice, puffed',
                'Roman Meal',
                'Seven-grain Cereal',
                'Shredded Wheat',
                "Shredded Wheat 'N Bran",
                'Shredded Wheat Spoon Size',
                'Shredded Wheat, 100%',
                'Shredded Wheat, Original',
                'Smacks',
                'Smart Start',
                'Smorz',
                'Special K',
                'Special K Fruit & Yogurt',
                'Special K Low Carb Lifestyle Protein Plus',
                'Special K Red Berries',
                'Special K Vanilla Almond',
                'Strawberry Squares',
                'Sun Country 100% Natural Granola, with Almonds',
                'Sweet Crunch',
                'Sweet Puffs',
                'Tasteeos',
                'Toasted Cinnamon Twists, Malt-O-Meal',
                'Toasted Oatmeal Cereal',
                'Toasted Oatmeal, Honey Nut',
                'Toasted oat cereal',
                'Toasties',
                "Toasty O's, Apple Cinnamon, Malt-O-Meal",
                "Toasty O's, Honey and Nut, Malt-O-Meal",
                "Toasty O's, Malt-O-Meal",
                "Tony's Cinnamon Crunchers",
                'Tootie Fruities, Malt-O-Meal',
                'Total',
                'Total Brown Sugar & Oats',
                'Total Corn Flakes',
                'Total Instant Oatmeal',
                'Total Raisin Bran',
                'Trix',
                'Trix, Reduced Sugar',
                "Uncle Sam's Hi Fiber Cereal",
                'Under Cover Bears',
                'Waffle Crisp',
                'Weetabix Whole Wheat Cereal',
                'Wheat Hearts',
                "Wheat bran, unprocessed (miller's bran)",
                'Wheat cereal',
                'Wheat germ',
                'Wheat germ, with sugar and honey',
                'Wheat, puffed',
                'Wheat, puffed, presweetened with sugar',
                'Wheatena',
                'Wheaties',
                'Wheaties Energy Crunch',
                'Wheaties Raisin Bran',
                'Whole wheat cereal',
                'Whole wheat, cracked',
                'Zoom',
                'Other Hot Cereal',
                'Other Cold Cereal',
                'N/A'
              ]
              // showOthers: 'Other',
              // text: {
              //   size: 'medium',
              //   horizontal: true,
              //   width: '50%'
              // }
            }
          },
          {
            type: 'Radio',
            questionId: 'nutrition_J84bZX',
            display: false,
            completed: false,
            details: {
              page: 1,
              question: 'Was there another cereal that you usually ate?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_kTMqU4'] },
                Yes: { show: ['nutrition_kTMqU4'] }
              }
            }
          },
          {
            type: 'Dropdown',
            questionId: 'nutrition_kTMqU4',
            customClassOther: 'survey-item-large',
            display: false,
            details: {
              page: 3,
              question: '',
              label: 'Search cereals',
              size: 'full',
              searchable: true,
              placeholder: 'Select Cereals',
              options: cerealsList
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_2',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_V2glFo',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you have any milk (either to drink or on cereal)? Include regular milks, chocolate or other flavored milks, lactose-free milk, buttermilk. Do not include soy milk or small amounts of milk in coffee or tea.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_67QauD', 'nutrition_xpezBC'] },
                Yes: { show: ['nutrition_67QauD', 'nutrition_xpezBC'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_67QauD',
            display: false,
            completed: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you have any milk (either to drink or on cereal)? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          },
          {
            type: 'Radio',
            questionId: 'nutrition_xpezBC',
            display: false,
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, what kind of milk did you usually drink?',
              options: [
                'Whole or regular milk',
                'Fat-free, skim or nonfat milk',
                '2% fat or reduced-fat milk',
                '1%, ½% or low-fat milk',
                'Soy milk',
                'Other'
              ],
              showOthers: 'Other',
              text: {
                size: 'medium',
                horizontal: true,
                width: '18%',
                required: true
              },
              fullWidth: true,
              routeMap: {
                No: { hide: [2, 3], goTo: 4 },
                Yes: { goTo: 2 }
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_3',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_J4gTMW',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you drink any regular soda or pop that contains sugar? Do not include diet soda.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_4SyCGC'] },
                Yes: { show: ['nutrition_4SyCGC'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_4SyCGC',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you drink regular soda or pop? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_4',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_J84bZA',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you drink any 100% pure fruit juices such as orange, mango, apple, grape and pineapple juices? Do not include fruit-flavored drinks with added sugar or fruit juice you made at home and added sugar to.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_bog1CJ'] },
                Yes: { show: ['nutrition_bog1CJ'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_bog1CJ',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you drink 100% pure fruit juice? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_5',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_8vbTtJ',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you drink any coffee or tea that had sugar or honey added to it? Include coffee and tea you sweetened yourself and presweetened tea and coffee drinks such as Arizona Iced Tea and Frappuccino. Do not include artificially sweetened coffee or diet tea.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_mqL5ZX'] },
                Yes: { show: ['nutrition_mqL5ZX'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_mqL5ZX',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you drink coffee or tea containing sugar or honey? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_6',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_2fNUXB',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you drink any sweetened fruit drinks, sports or energy drinks, such as Kool-aid, lemonade, Hi-C, cranberry drink, Gatorade, Red Bull, or Vitamin Water? Include fruit juices you made at home and added sugar to. Do not include diet drinks or artificially sweetened drinks.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_4U9Vn7'] },
                Yes: { show: ['nutrition_4U9Vn7'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_4U9Vn7',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you drink sweetened fruit, sports or energy drinks? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_7',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_ZcNULP',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any fruit? Include fresh, frozen, or canned fruit. Do not include juices.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_P9I5Ed'] },
                Yes: { show: ['nutrition_P9I5Ed'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_P9I5Ed',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat fruit? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_8',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_5q97bb',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat a green leafy or lettuce salad, with or without other vegetables?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_M8SeGx'] },
                Yes: { show: ['nutrition_M8SeGx'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_M8SeGx',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat salad? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_9',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_UVP7lM',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any kind of fried potatoes including french fries, home fries, or hash brown potatoes?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_N97MrY'] },
                Yes: { show: ['nutrition_N97MrY'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_N97MrY',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat any kind of fried potatoes? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_10',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_ep7OPF',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any other kind of potatoes, such as baked, boiled, mashed potatoes, sweet potatoes, or potato salad?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_bJx8Gh'] },
                Yes: { show: ['nutrition_bJx8Gh'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_bJx8Gh',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat any other kind of potatoes? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_11',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_6Ynw8F',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any refried beans, baked beans, beans in soup, pork and beans or other cooked dried beans? Do not include green beans.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_TFctrv'] },
                Yes: { show: ['nutrition_TFctrv'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_TFctrv',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat refried beans, baked beans, beans in soup, pork and beans or other cooked dried beans? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_12',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_iKc4BJ',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any brown rice or other cooked whole grains, such as bulgur, cracked wheat, or millet? Do not include white rice.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_oPWD8S'] },
                Yes: { show: ['nutrition_oPWD8S'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_oPWD8S',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat brown rice or other cooked whole grains? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_13',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_wHcudy',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, not including green salads, potatoes, and cooked dried beans, did you eat any other vegetables?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_exPEfz'] },
                Yes: { show: ['nutrition_exPEfz'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_exPEfz',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat other vegetables? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_14',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_km9Rdt',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any Mexican-type salsa made with tomato?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_SoBReN'] },
                Yes: { show: ['nutrition_SoBReN'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_SoBReN',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you have Mexican-type salsa made with tomato? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_15',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_MP3atb',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any pizza? Include frozen pizza, fast food pizza, and homemade pizza.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_Q5VSD0'] },
                Yes: { show: ['nutrition_Q5VSD0'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_Q5VSD0',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat pizza? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_16',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_sbn1cg',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you have any tomato sauces such as with spaghetti or noodles or mixed into foods such as lasagna? (Do not count tomato sauce on pizza.)',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_6LvKpx'] },
                Yes: { show: ['nutrition_6LvKpx'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_6LvKpx',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you have tomato sauces? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_17',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_qqbyj5',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any kind of cheese? Include cheese as a snack, cheese on burgers, sandwiches, and cheese in foods such as lasagna, quesadillas, or casseroles. Do not include cheese on pizza.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_3sXFHj'] },
                Yes: { show: ['nutrition_3sXFHj'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_3sXFHj',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat any kind of cheese? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_18',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_VRtPvr',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any red meat, such as beef, pork, ham, or sausage? Do not include chicken, turkey, or seafood. Include red meat you had in sandwiches, lasagna, stew, and other mixtures. Red meats may also include veal, lamb, and any lunch meats made with these meats.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_2NfZqe'] },
                Yes: { show: ['nutrition_2NfZqe'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_2NfZqe',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat red meat? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_19',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_4DLE18',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any processed meat, such as bacon, lunch meats, or hot dogs? Include processed meats you had in sandwiches, soups, pizza, casseroles, and other mixtures. <br/><br/>Processed meats are those preserved by smoking, curing, or salting, or by the addition of preservatives. Examples are: ham, bacon, pastrami, salami, sausages, bratwursts, frankfurters, hot dogs, and spam.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_kNMPeN'] },
                Yes: { show: ['nutrition_kNMPeN'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_kNMPeN',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat processed meat? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_20',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_UsRYFm',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any whole grain bread including toast, rolls and in sandwiches? Whole grain breads include whole wheat, rye, oatmeal and pumpernickel. Do not include white bread.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_G7lxSF'] },
                Yes: { show: ['nutrition_G7lxSF'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_G7lxSF',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat whole grain bread? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_21',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_Bn5Gkh',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any chocolate or any other types of candy? Do not include sugar-free candy.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_clYBxd'] },
                Yes: { show: ['nutrition_clYBxd'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_clYBxd',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat chocolate or any other types of candy? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_22',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_hLC2Kz',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any doughnuts, sweet rolls, Danish, muffins, (pan dulce) or pop-tarts? Do not include sugar-free items.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_hcKugV'] },
                Yes: { show: ['nutrition_hcKugV'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_hcKugV',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat doughnuts, sweet rolls, Danish, muffins, (pan dulce) or pop-tarts? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_23',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_zhT0va',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any cookies, cake, pie, or brownies? Do not include sugar-free kinds.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_PnBsjt'] },
                Yes: { show: ['nutrition_PnBsjt'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_PnBsjt',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat cookies, cake, pie, or brownies? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_24',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_MxfStK',
            completed: false,
            details: {
              page: 1,
              question:
                'During the past month, did you eat any ice cream or other frozen desserts? Do not include sugar-free kinds.',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_KAWmWJ'] },
                Yes: { show: ['nutrition_KAWmWJ'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_KAWmWJ',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat ice cream or other frozen desserts? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      },
      {
        stepId: 'nutrition_step_25',
        completed: false,
        questions: [
          {
            type: 'Radio',
            questionId: 'nutrition_JisRpw',
            completed: false,
            details: {
              page: 1,
              question: 'During the past month, did you eat any popcorn?',
              options: ['Yes', 'No'],
              horizontal: true,
              // fullWidth: true,
              routeMap: {
                No: { hide: ['nutrition_4BhlFg'] },
                Yes: { show: ['nutrition_4BhlFg'] }
              }
            }
          },
          {
            type: 'EditSelect',
            questionId: 'nutrition_4BhlFg',
            display: false,
            details: {
              customDDWidth: '200px',
              page: 0,
              notRequired: true,
              question:
                'During the past month, how often did you eat popcorn? You can report per day, per week, or per month.',
              text: {
                size: 'tiny',
                width: '5%'
              },
              dropdown: {
                options: ['times per day', 'times per week', 'times per month']
              }
            }
          }
        ]
      }
    ],
    questions: [],
    answers: {
      example: {
        cereal: { freq: 0.2, names: 'Kashi' },
        milk: { freq: 1.3, type: 'Whole or regular milk' },
        soda: 2.3,
        fruitJuice: 4.3,
        coffee: 1.2,
        sweetDrinks: 0.5,
        fruit: 1.3,
        salad: 4,
        potatoesFried: 0.3,
        potatoesOther: 0.5,
        beans: 1.5,
        grains: 1,
        vegetables: 2,
        salsa: 0.2,
        pizza: 0.6,
        tomatoSauce: 0.1,
        cheese: 1,
        redMeat: 1.3,
        proccessedMeat: 1,
        wholeGrainBread: 2,
        candy: 0.5,
        doughnuts: 1.3,
        cookies: 0.3,
        frozenDesserts: 0.5,
        popcorn: 2
      },
      raw: [],
      parsed: {
        cereal: {
          freqency: 'nutrition_mOwz3p',
          names: ['nutrition_kjBxU1', 'nutrition_kTMqU4']
        },
        milk: { freqency: 'nutrition_67QauD' },
        soda: { freqency: 'nutrition_4SyCGC' },
        fruitJuice: { freqency: 'nutrition_bog1CJ' },
        coffee: { freqency: 'nutrition_mqL5ZX' },
        sweetDrinks: { freqency: 'nutrition_4U9Vn7' },
        fruit: { freqency: 'nutrition_P9I5Ed' },
        salad: { freqency: 'nutrition_M8SeGx' },
        potatoesFried: { freqency: 'nutrition_N97MrY' },
        potatoesOther: { freqency: 'nutrition_bJx8Gh' },
        beans: { freqency: 'nutrition_TFctrv' },
        grains: { freqency: 'nutrition_oPWD8S' },
        vegetables: { freqency: 'nutrition_exPEfz' },
        salsa: { freqency: 'nutrition_SoBReN' },
        pizza: { freqency: 'nutrition_Q5VSD0' },
        tomatoSauce: { freqency: 'nutrition_6LvKpx' },
        cheese: { freqency: 'nutrition_3sXFHj' },
        redMeat: { freqency: 'nutrition_2NfZqe' },
        proccessedMeat: { freqency: 'nutrition_kNMPeN' },
        wholeGrainBread: { freqency: 'nutrition_G7lxSF' },
        candy: { freqency: 'nutrition_clYBxd' },
        doughnuts: { freqency: 'nutrition_hcKugV' },
        cookies: { freqency: 'nutrition_PnBsjt' },
        frozenDesserts: { freqency: 'nutrition_KAWmWJ' },
        popcorn: { freqency: 'nutrition_4BhlFg' }
      }
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  },
  mentalHealth: {
    title: 'Mental Health',
    route: '/me/assessment/mental-health',
    shortName: 'mentalHealth',
    completed: false,
    order: 8,
    timeToComplete: '2min',
    _meta: {
      route: '/landing/assessments/mentalHealth'
    },
    steps: [
      {
        stepId: 'mentalhealth_step_0',
        completed: false,
        questions: [
          {
            type: 'RadioGrid',
            questionId: 'mentalhealth_c4dUEu',
            details: {
              page: 0,
              question:
                'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
              xLabels: [
                'Not at all',
                'Several days',
                'More than half the days',
                'Nearly every day'
              ],
              yLabels: [
                'Little interest or pleasure in doing things',
                'Feeling down, depressed, or hopeless',
                'Trouble falling or staying asleep, or sleeping too much',
                'Feeling tired or having little energy',
                'Poor appetite or overeating',
                'Feeling bad about yourself -- or that you are a failure or have let yourself or your family down',
                'Trouble concentrating on things, such as reading the newspaper or watching television',
                'Moving or speaking so slowly that other people could have noticed? Or the opposite -- being so fidgety or restless that you have been moving around a lot more than usual',
                'Thoughts that you would be better off dead or of hurting yourself in some way'
              ]
            }
          },
          {
            type: 'Radio',
            questionId: 'mentalhealth_gjD2tj',
            details: {
              page: 0,
              question:
                'If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
              options: [
                'Not difficult at all',
                'Somewhat difficult',
                'Very difficult',
                'Extremely Difficult'
              ]
            }
          }
        ]
      }
    ],
    answers: {
      example: {
        doingThings: 'Not at all',
        feelingDown: 'Several days',
        fallingAsleep: 'Not at all',
        feelingTired: 'Not at all',
        poorAppetite: 'Nearly every day',
        feelingBad: 'Not at all',
        troubleConcentrating: 'Not at all',
        movingSlowly: 'More than half the days',
        thoughtsOfDeath: 'Not at all',
        thingsAtHome: 'Not at all'
      },
      raw: [],
      parsed: {
        doingThings: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey: 'Little interest or pleasure in doing things'
        },
        feelingDown: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey: 'Feeling down, depressed, or hopeless'
        },
        fallingAsleep: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey: 'Trouble falling or staying asleep, or sleeping too much'
        },
        feelingTired: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey: 'Feeling tired or having little energy'
        },
        poorAppetite: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey: 'Poor appetite or overeating'
        },
        feelingBad: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey:
            'Feeling bad about yourself -- or that you are a failure or have let yourself or your family down'
        },
        troubleConcentrating: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey:
            'Trouble concentrating on things, such as reading the newspaper or watching television'
        },
        movingSlowly: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey:
            'Moving or speaking so slowly that other people could have noticed? Or the opposite -- being so fidgety or restless that you have been moving around a lot more than usual'
        },
        thoughtsOfDeath: {
          questionId: 'mentalhealth_c4dUEu',
          answerKey:
            'Thoughts that you would be better off dead or of hurting yourself in some way'
        },
        thingsAtHome: {
          questionId: 'mentalhealth_gjD2tj',
          answerKey: 'selected'
        }
      }
    },
    farthestProgress: 0,
    percent: 0,
    progress: 0
  }
};
